import { Button, Modal, Select, Skeleton, Tooltip, Typography } from "antd";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import PermissionCheck from "~Common/PermissionCheck";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { AddStandingModal } from "~pages/Standings/components/AddStandingModal";
import { StandingColumnsSelect } from "~pages/Standings/components/StandingColumnsSelect";
import StandingList from "~pages/Standings/components/StandingList";
import StandingsConference from "~pages/Standings/components/StandingsConference";
import StandingsStage from "~pages/Standings/components/StandingsStage";
import { GroupingBase } from "~pages/Standings/enums/groupingBase";
import { GroupingConferenceDivision } from "~pages/Standings/enums/groupingConferenceDivision";
import { GroupingStagePool } from "~pages/Standings/enums/groupingStagePool";
import { useBuildStandings } from "~pages/Standings/hooks/useBuildStandings";
import { useCreateStanding } from "~pages/Standings/hooks/useCreateStanding";
import { useDeleteStandings } from "~pages/Standings/hooks/useDeleteStandings";
import { useFetchAllStandings } from "~pages/Standings/hooks/useFetchAllStandings";
import { useGetSeasonStandingConfiguration } from "~pages/Standings/hooks/useGetSeasonStandingConfiguration";
import type { StandingTransformed } from "~pages/Standings/types/standingTransformed";
import type { TransformedResult } from "~pages/Standings/types/transformedResult";
import { getStandingGroupings } from "~pages/Standings/utilities/getStandingGroupings";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;

  .standing-controls {
    margin-left: auto;
  }
`;

const StandingsPage = () => {
  const { data: standingConfiguration, isLoading: isStandingConfigurationLoading } = useGetSeasonStandingConfiguration();
  const { formatMessage } = useIntl();
  const { sport, organizationId, seasonId, customerId } = useParams();
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const standingsGrouping = getStandingGroupings(standingConfiguration);
  const [grouping, setGrouping] = useState<GroupingStagePool>();
  const [conferenceGrouping, setConferenceGrouping] = useState<GroupingConferenceDivision>();

  const params = {
    offset: "0",
    limit: "1000",
    groupingStagePool: grouping,
    groupingConferenceDivision: conferenceGrouping,
    groupingBase: GroupingBase.Overall,
    include: ["seasonStages", "seasonPools", "conferences", "divisions", "entities", "persons"].join(","),
  };

  const { data, isLoading } = useFetchAllStandings(
    params,
    !isStandingConfigurationLoading && !!grouping && !!conferenceGrouping,
    grouping,
    conferenceGrouping,
  );

  const { mutateAsync: buildStandings, isPending: isBuildPending } = useBuildStandings();
  const { mutateAsync: deleteStandings, isPending: isDeletePending } = useDeleteStandings();
  const { mutateAsync: createStanding, isPending: isCreatePending } = useCreateStanding(params);
  const [modal, contextHolder] = Modal.useModal();

  const handleDelete = () => {
    modal.confirm({
      title: formatMessage({ id: "standings.delete" }),
      content: formatMessage({ id: "standings.delete.confirm" }),
      okText: formatMessage({ id: "delete" }),
      okButtonProps: { loading: false },
      onOk: () => deleteStandings(),
    });
  };

  if (standingConfiguration && !grouping && !conferenceGrouping) {
    const standingsGrouping = getStandingGroupings(standingConfiguration);
    setGrouping(standingsGrouping?.STAGE_POOL.defaultValue);
    setConferenceGrouping(standingsGrouping?.CONFERENCE_DIVISION.defaultValue);
  }

  const loading = isStandingConfigurationLoading || isLoading || isBuildPending || isDeletePending;

  return (
    <>
      {contextHolder}
      <StyledContainer>
        <Typography.Title style={{ order: 3, marginBottom: 0, height: 32 }} level={4}>
          {" "}
          {formatMessage({ id: "standings" })}{" "}
        </Typography.Title>
        <span className="standing-controls" style={{ display: "flex", gap: 10, order: 1 }}>
          <StandingColumnsSelect disabled={loading} columns={selectedColumns} setColumns={setSelectedColumns} />
          <PermissionCheck permissionKeys={[PermissionNames.STANDING_ADJUSTMENT]}>
            <Tooltip title={formatMessage({ id: "standings.adjustments" })}>
              <Button disabled={loading} type="text">
                <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/standings/adjustments`}>
                  <FormattedMessage id="standings.adjustments" />
                </Link>
              </Button>
            </Tooltip>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionNames.STANDING]} permissionOptions={[PermissionOperations.CREATE]}>
            <Button
              type="primary"
              disabled={loading}
              onClick={() => {
                buildStandings(standingConfiguration);
              }}
            >
              <FormattedMessage id={"buildStandings"} />
            </Button>
            <AddStandingModal disabled={loading} createStanding={createStanding} isCreatePending={isCreatePending} />
            <PermissionCheck permissionKeys={[PermissionNames.STANDING]} permissionOptions={[PermissionOperations.DELETE]}>
              <Button type="primary" disabled={loading} onClick={handleDelete}>
                <FormattedMessage id={"standings.delete"} />
              </Button>
            </PermissionCheck>
          </PermissionCheck>
        </span>
        <span style={{ display: "flex", gap: 10, order: 2 }}>
          {standingsGrouping.STAGE_POOL.values.length > 1 && (
            <span style={{ marginLeft: 10 }}>
              <FormattedMessage id={"standings.table.grouping.stage"} />
              <Select
                disabled={loading}
                defaultValue={standingsGrouping?.STAGE_POOL.defaultValue}
                value={grouping}
                onSelect={(value) => {
                  setGrouping(value);
                  setConferenceGrouping(GroupingConferenceDivision.Overall);
                }}
                style={{ width: 100, marginLeft: 10 }}
                options={standingsGrouping.STAGE_POOL.values.map((item) => ({
                  label: item,
                  value: item,
                }))}
              />
            </span>
          )}
          {standingsGrouping.CONFERENCE_DIVISION.values.length > 1 && (
            <span style={{ marginLeft: 10 }}>
              <FormattedMessage id={"standings.table.grouping.conf"} />
              <Select
                disabled={loading}
                value={conferenceGrouping}
                onSelect={(value) => {
                  setConferenceGrouping(value);
                  setGrouping(GroupingStagePool.Overall);
                }}
                style={{ width: 100, marginLeft: 10 }}
                options={standingsGrouping.CONFERENCE_DIVISION.values.map((item) => ({ label: item, value: item }))}
              />
            </span>
          )}
        </span>
        {loading && <Skeleton style={{ order: 4 }} active />}
        {!loading && (
          <>
            {grouping && (grouping === GroupingStagePool.StagePool || grouping === GroupingStagePool.Stage) && (
              <StandingsStage
                stages={data as Array<TransformedResult<StandingTransformed | TransformedResult<StandingTransformed>>>}
                grouping={grouping}
                selectedColumns={selectedColumns}
              />
            )}
            {conferenceGrouping &&
              (conferenceGrouping === GroupingConferenceDivision.Conference ||
                conferenceGrouping === GroupingConferenceDivision.Division) && (
                <StandingsConference
                  conferences={data as Array<TransformedResult<StandingTransformed | TransformedResult<StandingTransformed>>>}
                  grouping={conferenceGrouping}
                  selectedColumns={selectedColumns}
                />
              )}
            {grouping &&
              conferenceGrouping &&
              grouping === GroupingStagePool.Overall &&
              conferenceGrouping === GroupingConferenceDivision.Overall && (
                <span style={{ flexBasis: "100%", order: 4, width: "100%" }}>
                  <StandingList selectedColumns={selectedColumns} data={data as Array<StandingTransformed>} isLoading={loading} />
                </span>
              )}
          </>
        )}
      </StyledContainer>
    </>
  );
};

export default StandingsPage;
